import {useCallback, useEffect, useMemo, useState} from 'react';

import {ACCESS_RIGHTS, useAdminAccessRight} from 'src/hooks/access-rights';
import {useConferenceAccountCheck} from 'src/hooks/useConferenceAccountCheck';
import {VALIDATION_FAILED} from 'src/modules/create-account/constants';
import {getLicense} from 'src/openapi/admin/apis/GetLicenseApi';
import {saveLicenseBaseInfo} from 'src/openapi/admin/apis/SaveLicenseBaseInfoApi';
import {LicenseType as LicenseTypes} from 'src/openapi/admin/models/LicenseType';
import {useAppDispatch} from 'src/store/hooks';
import {getError} from 'src/utils/get-error';
import {isDeepEqual} from 'src/utils/isDeepEqual';

import {DEFAULT_CREATE_LICENSE_DATA} from './constants';
import {useValidateLicense} from './useValidateLicense';

import type {LicenseUpdateRequest} from 'src/openapi/admin/models/LicenseUpdateRequest';

type Props = {
    accountId: number,
    accountEntityType: string,
    templateLicenseId?: number,
};

export const useLicense = ({accountId, templateLicenseId, accountEntityType}: Props) => {
    const apiDispatch = useAppDispatch();
    const isConferenceAccount = useConferenceAccountCheck();
    const {hasAccess} = useAdminAccessRight();

    const defaultLicense = useMemo(() => {
        if (isConferenceAccount && accountEntityType === 'Event') {
            return {
                ...DEFAULT_CREATE_LICENSE_DATA,
                licenseType: LicenseTypes.CN,
            };
        }

        if (!hasAccess(ACCESS_RIGHTS.ALL_LICENSE_TYPES_ACCESS) || accountEntityType === 'Trial') {
            return {
                ...DEFAULT_CREATE_LICENSE_DATA,
                licenseType: LicenseTypes.TR,
            };
        }
        return DEFAULT_CREATE_LICENSE_DATA;
    }, [isConferenceAccount, accountEntityType, hasAccess]);

    const [licenseLoading, setLicenseLoading] = useState(false);
    const [initialLicense, setInitialLicense] = useState<LicenseUpdateRequest>(defaultLicense);
    const [license, setLicense] = useState<LicenseUpdateRequest>(defaultLicense);

    const {validateLicenseRequest, hasValidationErrors} = useValidateLicense();

    useEffect(() => {
        if (templateLicenseId) {
            setLicenseLoading(true);
            apiDispatch(getLicense({licenseId: +templateLicenseId})).then(response => {
                if (response.payload.success) {
                    const baseLicenseInformation: LicenseUpdateRequest = {
                        licenseType: response.payload.data.typeCode,
                        concurrentUserCount: response.payload.data.concurrentUserCount,
                        startDate: '',
                        endDate: '',
                    };
                    setInitialLicense(baseLicenseInformation);
                    setLicense(baseLicenseInformation);
                }

                setLicenseLoading(false);
            });
        }
    }, [templateLicenseId, apiDispatch]);

    useEffect(() => {
        validateLicenseRequest(license);
    }, [license, validateLicenseRequest]);

    const createLicense = useCallback(async (
        onSuccess: (id: number) => void,
        onFailure: (error: string) => void, validationError?: boolean,
    ) => {
        const {startDate, endDate} = license;

        const invalidRequest =
            validateLicenseRequest(license, true) ||
            hasValidationErrors ||
            validationError;

        if (invalidRequest) {
            onFailure(VALIDATION_FAILED);
            return;
        }

        if (startDate && endDate) {
            const saveLicenseResponse = await apiDispatch(saveLicenseBaseInfo({
                accountId,
                licenseUpdateRequest: license,
            }));

            if (saveLicenseResponse.payload.error) {
                onFailure(getError({error: saveLicenseResponse.payload.data, separator: ' '}));
            } else {
                setInitialLicense(license);
                onSuccess(saveLicenseResponse.payload.data);
            }
        }
    }, [license, accountId, hasValidationErrors, apiDispatch, validateLicenseRequest]);

    const isInitialLicense = useMemo(() => (
        isDeepEqual({current: initialLicense, prev: license})
    ), [initialLicense, license]);

    return {
        licenseLoading,
        initialLicense,
        license,
        setLicense,
        createLicense,
        isInitialLicense,
    };
};
